import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  Popover,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from "@mui/material";
import { useContext, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { CampaignContext } from "../Context";
import CustomTextInput from "global/components/CustomTextInput/CustomTextInput";
import InboxPreview from "./InboxPreview";
import EmojiPicker from "emoji-picker-react";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import { cloneDeep } from "lodash";
import { isTruthy } from "helpers/methods";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import SuggestionBox from "screens/Shared/SuggestionBox";
import { useAppSelector } from "utils/hooks";
import { selectIsFirstNameMandatory } from "redux/generalSlice";

const SubjectSection = () => {
  const [openEmojiPicker, setOpenEmojiPicker] =
    useState<HTMLButtonElement | null>(null);
  const {
    submitCampaign,
    setSubmitCampaign,
    managingStep,
    setManagingStep,
    saveAsDraft,
  } = useContext(CampaignContext);
  const [formFields, setFormFields] = useState({
    subject: {
      value: submitCampaign.campaign.subject ?? "",
      error: "",
    },
  });
  const [saving, setSaving] = useState<boolean>(false);
  const isFirstNameMandatory = useAppSelector(selectIsFirstNameMandatory);

  const handleCancel = () => {
    setManagingStep("");
    setFormFields({
      subject: {
        value: submitCampaign.campaign.subject ?? "",
        error: "",
      },
    });
  };

  const handleSave = async () => {
    let error = false;
    const clonedFormFields = cloneDeep(formFields);
    const subjectValue = clonedFormFields.subject.value.trim();
    if (!subjectValue) {
      error = true;
      clonedFormFields.subject.error = "Please enter the subject";
    }
    if (
      subjectValue &&
      isFirstNameMandatory &&
      !subjectValue.includes("$FIRST_NAME")
    ) {
      error = true;
      clonedFormFields.subject.error = "Subject must include $FIRST_NAME";
    }
    if (error) {
      return setFormFields(clonedFormFields);
    }
    setSaving(true);
    const campaign = {
      ...submitCampaign,
      campaign: {
        ...submitCampaign.campaign,
        subject: subjectValue,
      },
    };
    setSubmitCampaign(campaign);
    await saveAsDraft(campaign);
    setManagingStep("");
    setSaving(false);
  };

  const getExpandedView = () => {
    return (
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" spacing={4} width="100%">
            <SvgIcon>
              <CheckCircleIcon
                htmlColor={stepCompletion() ? "#7551D0" : "#C1C1C1"}
              />
            </SvgIcon>
            <Stack spacing={2} width="100%">
              <Stack spacing={0.5}>
                <Typography variant="h4">Subject</Typography>
                <Typography variant="body1">
                  Create a compelling subject to boost open rates
                </Typography>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <CustomTextInput
                    label="Subject Line"
                    placeHolder="Enter your subject line"
                    value={formFields.subject.value}
                    onChange={(event: any) => {
                      setFormFields({
                        ...formFields,
                        subject: {
                          value: event.target.value,
                          error: "",
                        },
                      });
                    }}
                    required
                    error={formFields.subject.error}
                    propsToInputElement={{ minLength: 1, maxLength: 90 }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <span style={{ fontSize: "0.75rem" }}>
                            {formFields.subject?.value?.length} / 90
                          </span>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Stack direction="row" justifyContent="flex-end">
                    <Tooltip title="Add emojis">
                      <IconButton
                        onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
                          setOpenEmojiPicker(event.currentTarget)
                        }
                      >
                        <SvgIcon>
                          <EmojiEmotionsIcon
                            htmlColor="#7551D0"
                            fontSize="small"
                          />
                        </SvgIcon>
                      </IconButton>
                    </Tooltip>
                    <Popover
                      open={Boolean(openEmojiPicker)}
                      anchorEl={openEmojiPicker}
                      onClose={() => setOpenEmojiPicker(null)}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                      }}
                    >
                      <EmojiPicker
                        onEmojiClick={(emoji) => {
                          setFormFields({
                            ...formFields,
                            subject: {
                              value: `${formFields.subject.value}${emoji.emoji}`,
                              error: "",
                            },
                          });
                        }}
                      />
                    </Popover>
                  </Stack>
                  {isFirstNameMandatory && (
                    <SuggestionBox title="$FIRST_NAME is required to enhance personalization." />
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <InboxPreview
                    for={"subject"}
                    string={formFields.subject.value}
                  />
                </Grid>
              </Grid>
            </Stack>
          </Stack>
          <Stack>
            <IconButton onClick={handleCancel}>
              <SvgIcon>
                <CloseIcon />
              </SvgIcon>
            </IconButton>
          </Stack>
        </Stack>
        <Stack
          alignItems="center"
          justifyContent="flex-end"
          direction="row"
          spacing={1}
        >
          <Button size="small" variant="text" onClick={handleCancel}>
            Cancel
          </Button>
          <Button size="small" variant="contained" onClick={handleSave}>
            Save
          </Button>
        </Stack>
      </Stack>
    );
  };

  const stepCompletion = () => {
    return isTruthy(submitCampaign.campaign.subject);
  };

  const getNonExpandedView = () => {
    const onAnotherStep =
      managingStep !== "" && managingStep !== "subject-section";
    return (
      <Grid
        container
        gap={1}
        justifyContent="space-between"
        sx={{ opacity: onAnotherStep ? 0.5 : 1 }}
      >
        <Stack direction="row" spacing={4}>
          <SvgIcon>
            <CheckCircleIcon
              htmlColor={stepCompletion() ? "#7551D0" : "#C1C1C1"}
            />
          </SvgIcon>
          <Stack spacing={0.5}>
            <Typography variant="h4">Subject</Typography>
            {isTruthy(submitCampaign.campaign.subject) ? (
              <Tooltip title={submitCampaign.campaign.subject} arrow>
                <Typography
                  fontStyle="italic"
                  variant="body1"
                  noWrap
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                  maxWidth="300px"
                  display="block"
                >
                  {submitCampaign.campaign.subject}
                </Typography>
              </Tooltip>
            ) : (
              <Typography variant="body1">
                Create a compelling subject to boost open rates
              </Typography>
            )}
          </Stack>
        </Stack>
        <Stack>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setManagingStep("subject-section");
            }}
            disabled={onAnotherStep}
          >
            {`${stepCompletion() ? "Edit" : "Add"}`} Subject
          </Button>
        </Stack>
      </Grid>
    );
  };

  const getPage = () => {
    if (managingStep === "subject-section") {
      return getExpandedView();
    }
    return getNonExpandedView();
  };

  return (
    <>
      <Box px={3}>{getPage()}</Box>
      <CustomLoader isLoading={saving} />
    </>
  );
};

export default SubjectSection;
